import Highcharts, { PointOptionsObject } from 'highcharts';
import { SeriesBubbleOptions } from 'highcharts/highcharts';
import cloneDeep from 'lodash.clonedeep';
import { WidgetType } from 'types/widget.types';
import { DEFAULT_TEXT_STYLE } from 'widgets/ChartWidgets/ChartWidget.config';
import {
  CustomLegendRender,
  PrefixSuffixProps,
  applyPatterns,
  formatAxisDataLabel,
  formatDataLabel,
  getPercent,
  getPrefixSuffix,
  replaceGWithBFormatter,
} from 'widgets/ChartWidgets/ChartWidget.helpers';
import { HighchartsSeriesData } from 'widgets/ChartWidgets/ChartWidget.hooks';
import { HighchartsOptions } from 'widgets/ChartWidgets/DataTableChartCommon/DataTableChartCommon.types';
import {
  DEFAULT_BORDER_COLOR,
  DEFAULT_BORDER_WIDTH,
  DEFAULT_HEIGHT,
  DEFAULT_SCATTER_BUBBLE_CHART_DATA,
  DEFAULT_WIDTH,
} from 'widgets/ChartWidgets/ScatterBubbleChartWidget/ScatterBubbleChartWidget.config';
import { ScatterBubbleChartWidgetData } from 'widgets/ChartWidgets/ScatterBubbleChartWidget/ScatterBubbleChartWidget.types';
import { VERSION } from 'widgets/ChartWidgets/ScatterBubbleChartWidget/ScatterBubbleChartWidget.upgrade';

const DEFAULT_WIDGET_DATA = {
  version: VERSION,
  widthPx: DEFAULT_WIDTH,
  heightPx: DEFAULT_HEIGHT,
  topPx: 0,
  leftPx: 0,
  rotateDeg: 0,
  altText: '',
  isDecorative: false,
};

export const generateDefaultData = () => {
  return {
    widgetType: WidgetType.ScatterBubbleChart,
    widgetData: {
      ...DEFAULT_WIDGET_DATA,
      ...DEFAULT_SCATTER_BUBBLE_CHART_DATA,
    } as ScatterBubbleChartWidgetData,
  };
};

/**
 * @param defaultOptions Is modified by reference! Ensure that you're always passing a new object.
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 */
export const getHighChartsScatterBubbleConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: ScatterBubbleChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;
  const { enabled: patternsEnabled, list: patternsList } = widgetData.patterns;

  const {
    dataLabels: {
      enabled: enableDataLabel,
      prefix,
      suffix,
      style: dataLabelStyle,
      categoryEnabled,
      valueEnabled,
      inside,
    },
    generalOptions: { borderWidth = DEFAULT_BORDER_WIDTH, borderColor = DEFAULT_BORDER_COLOR } = {},
    legend: { enabled: enableLegend },
    numberFormat: { precision, separator, format } = {},
  } = widgetData;

  options.chart = options.chart || {};
  options.chart.type = 'bubble';
  options.chart.events = { render: CustomLegendRender };

  options.keyCustom = keyCustom;
  options.xAxis = (options.xAxis as Highcharts.XAxisOptions) || {};

  options.xAxis.categories = categories;

  options.yAxis = {
    ...((options.yAxis as Highcharts.YAxisOptions) || {}),
    labels: {
      ...((options.yAxis as Highcharts.YAxisOptions)?.labels || {}),
      formatter: function () {
        if (format === 'percentage') {
          return `${formatAxisDataLabel({
            dataLabel: replaceGWithBFormatter.call(this),
            prefix: '',
            suffix: '%',
          })}`;
        }
        return `${formatAxisDataLabel({
          dataLabel: replaceGWithBFormatter.call(this),
          prefix,
          suffix,
        })}`;
      },
    },
  };

  // Convert the categories to numbers if possible, if category isNaN, highcharts will remove the bubble from the chart
  const xData = categories.map((category) => (isNaN(Number(category)) ? category : Number(category)));

  // first item in series array is yAxis taken from 2nd column data in the datatables
  const ySeries = series[0] ? series[0] : null;
  const zSeries = series[1] ? series[1] : null;
  const yData = ySeries ? (ySeries.data as PointOptionsObject[])?.map((item) => item?.y) : null;
  const zData = zSeries ? (zSeries.data as PointOptionsObject[])?.map((item) => item?.y) : null;

  let bubbleAxisData = [];
  for (let i = 0; i < xData.length; i++) {
    // Construct bubbleAxisData in the following format:
    // [{ "x": 53, "y": 430, "z": null }, ...]
    const xValue = xData && xData[i] !== undefined ? xData[i] : null;
    const yValue = yData && yData[i] !== undefined ? yData[i] : null;
    const zValue = zData && zData[i] !== undefined ? zData[i] : null;

    bubbleAxisData.push({ x: xValue, y: yValue, z: zValue });
  }

  options.series = [
    {
      name: series[0]?.name,
      color: series[0]?.color,
      data: cloneDeep(bubbleAxisData), // Avoid modifying the original when applying patterns
      visible: true,
      states: { hover: { enabled: false } },
      type: 'bubble',
    },
  ] as SeriesBubbleOptions[];

  const animation = options.plotOptions?.series?.animation || false;
  options.plotOptions = {};
  options.plotOptions.series = {
    enableMouseTracking: false,
    shadow: false,
    animation,
    dataLabels: {
      inside,
    },
  };

  const isCategoryEnabled = typeof categoryEnabled === 'undefined' ? enableDataLabel : categoryEnabled;
  const isValueEnabled = typeof valueEnabled === 'undefined' ? enableDataLabel : valueEnabled;
  const isEnableLabel = isCategoryEnabled || isValueEnabled;

  options.plotOptions.bubble = {
    allowPointSelect: true,
    cursor: 'pointer',
    showInLegend: enableLegend,
    borderWidth,
    borderColor,
    dataLabels: {
      enabled: isEnableLabel,
      style: {
        ...DEFAULT_TEXT_STYLE,
        textOverflow: 'wrap',
        color: dataLabelStyle.color,
        fontSize: dataLabelStyle.fontSize,
        fontFamily: dataLabelStyle.fontFamily,
        fontWeight: dataLabelStyle.fontWeight,
        fontStyle: dataLabelStyle.fontStyle,
        textDecoration: dataLabelStyle.textDecoration,
      },
      formatter: function () {
        if (format === 'percentage') {
          const adjustedPrecision = precision === undefined || precision === 'Auto' ? '0' : precision;
          const percent = getPercent({ series: this.series, value: this.y || 0 });
          return `${formatDataLabel({ dataLabel: percent, precision: adjustedPrecision, separator })}%`;
        }

        const { prefix: customPrefix = prefix, suffix: customSuffix = suffix } = getPrefixSuffix({
          data: ySeries?.data?.[this.point.index],
          prefix,
          suffix,
        } as PrefixSuffixProps);

        // Use the 4th column from datatables as category when multi-series scatter bubble chart is enabled
        if (isCategoryEnabled && isValueEnabled) {
          return `${formatDataLabel({
            dataLabel: this.y,
            prefix: customPrefix,
            suffix: customSuffix,
            precision,
            separator,
          })}`;
        }

        if (!isCategoryEnabled && isValueEnabled) {
          return `${formatDataLabel({
            dataLabel: this.point.y,
            prefix: customPrefix,
            suffix: customSuffix,
            precision,
            separator,
          })}`;
        }

        if (isCategoryEnabled && !isValueEnabled) {
          return `${this.point.y}`;
        }

        return `${formatDataLabel({
          dataLabel: this.point.y,
          prefix: customPrefix,
          suffix: customSuffix,
          precision,
          separator,
        })}`;
      },
    },
  };

  if (patternsEnabled) {
    applyPatterns(options, patternsList);
  }

  return options;
};
